export const CHECKOUT_URL = "https://implanning-en.impultienda.ar/checkout/";

export const images = {
  main: "https://cdn.magicpatterns.com/uploads/2pG8segxuj3cqGJsuWwZNQ/IMPLANNING-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/jX5f9ypgvthi2zs7WDsCN4/MICHAEL_CARTER_-_IMPLANTES.png",
};

export const stats = [
  { value: "+40", label: "Clinical planning tools" },
  { value: "6", label: "Step-by-step volumes" },
  { value: "30+", label: "Decision frameworks & checklists" },
  { value: "20 MIN", label: "Target planning workflow" },
];

export const beforeItems = [
  "You look at a new implant case and aren't sure where to start.",
  "You jump between notes, courses, screenshots and references before making a plan.",
  "You worry about missing an anatomical or prosthetic consideration.",
  "You spend too much time reviewing a case before deciding whether you're ready to proceed.",
  "Every patient seems to require a completely different planning process.",
  "You know the theory, but translating it into a repeatable clinical workflow feels difficult.",
];

export const afterItems = [
  "You follow the same structured planning sequence for every case.",
  "You know what to evaluate before moving to the next decision.",
  "You use dedicated checklists to review anatomical and clinical risk factors.",
  "You connect diagnosis, CBCT, prosthetic considerations and surgical planning.",
  "You can organize the critical information before entering the surgical room.",
  "You develop a repeatable planning workflow instead of relying on memory alone.",
];

export const goalQuestions = [
  "What do I need to evaluate?",
  "What decision comes next?",
  "What information am I still missing?",
];

export const painPoints = [
  { icon: "frown", text: "You have a patient who wants an implant, but you're not completely sure how to structure the case evaluation." },
  { icon: "timer", text: "You spend too much time going back through notes, courses and references before planning." },
  { icon: "scan", text: "You look at a CBCT but aren't always sure which findings should influence your implant position." },
  { icon: "help", text: "You know the basic principles of implantology but struggle to turn them into a consistent planning sequence." },
  { icon: "dollar", text: "You know there are implant cases you could potentially take, but uncertainty makes you hesitate." },
  { icon: "alert", text: "You worry that one overlooked planning detail could create a problem later." },
  { icon: "brain", text: "Before a surgical case, you feel like you need to remember everything at once." },
];

export const volumes = [
  {
    numeral: "I",
    title: "The Implant Case Assessment System",
    subtitle: "Know What You're Dealing With Before You Start Planning",
    body: "Before thinking about implant position, I want you to know whether the case is ready to be planned at all. A structured framework for reviewing the patient's clinical situation, relevant risk factors, periodontal considerations and overall case complexity — so you can identify what requires further evaluation before moving forward.",
    bullets: [] as string[],
  },
  {
    numeral: "II",
    title: "The Clinical Risk & Case Selection Framework",
    subtitle: "Know Which Questions Must Be Answered Before You Commit to the Case",
    body: "Not every implant case should be approached in exactly the same way. Identify clinical risk factors, potential red flags and situations that may require additional preparation, modification or referral — and classify the case before investing time in surgical planning.",
    bullets: [] as string[],
  },
  {
    numeral: "III",
    title: "The CBCT Planning Roadmap",
    subtitle: "Turn a CBCT Into a Structured Planning Sequence",
    body: "A CBCT contains a tremendous amount of information. The goal isn't simply to \"read the CBCT\" — it's to connect what you see to the decisions you need to make.",
    bullets: ["Available bone", "Width and height", "Critical anatomy", "Anatomical limitations", "Safety considerations", "Implant position", "Implant axis", "Surgical implications"],
  },
  {
    numeral: "IV",
    title: "The Prosthetic-Driven Implant Planning System",
    subtitle: "Plan the Implant Around the Final Restoration — Not Just the Available Bone",
    body: "The implant doesn't exist by itself. It has to serve the final restoration. Learn to think backward from the desired restorative result by organizing the relationship between:",
    bullets: ["Final prosthetic objective", "Implant position", "Implant axis", "Available anatomy", "Esthetic considerations", "Functional considerations", "Surgical limitations"],
  },
  {
    numeral: "V",
    title: "The Surgical Preparation Playbook",
    subtitle: "Translate Your Planning Into a Clear Pre-Surgical Sequence",
    body: "Don't make the operating room the place where you finish making the plan. A structured pre-surgical workflow covering:",
    bullets: ["Final case review", "Surgical objectives", "Implant position", "Surgical sequence", "Preparation checklist", "Risk review", "Primary stability considerations", "Pre-operative verification"],
  },
  {
    numeral: "VI",
    title: "The Complete Implant Case Planning Workflow",
    subtitle: "Put Everything Together Into One Repeatable System",
    body: "Your everyday reference. Everything from the previous volumes combined into one complete workflow — plus the complete 20-Minute Implant Planning Workflow™, so you stop wondering \"What should I look at next?\"",
    bullets: ["Patient", "Diagnosis", "Risk", "Anatomy", "CBCT", "Prosthetic Goal", "Implant Position", "Surgical Plan", "Final Review"],
  },
];

export const tools = [
  { icon: "file", title: "40+ Implant Planning Protocols & Checklists", text: "Practical resources organized around diagnosis, risk assessment, CBCT review, planning and surgical preparation." },
  { icon: "layers", title: "Clinical Decision Frameworks", text: "Simple visual frameworks that help you organize the questions you need to answer before advancing the case." },
  { icon: "scan", title: "CBCT Planning Worksheets", text: "Structured pages for documenting the findings that matter to implant planning." },
  { icon: "grid", title: "Case Assessment Matrices", text: "Organize risk factors, anatomical considerations and planning decisions before committing to the surgical phase." },
  { icon: "ruler", title: "Implant Planning Worksheets", text: "Dedicated templates for position, axis, anatomy, prosthetic objectives and pre-surgical review." },
  { icon: "search", title: "Final Case Review Checklist", text: "A final structured pass through the case before surgery." },
];

export const bonuses = [
  {
    number: 1,
    title: "30 Implant Case Planning Scenarios™",
    value: "$47",
    image: "https://cdn.magicpatterns.com/uploads/4z4wuozyhHcBd3MXx25tgf/BONUS_1_IMPLANNING-EN.png",
    text: "30 educational case scenarios designed to make you practice the planning process. Don't just read about planning — practice thinking through cases.",
    bullets: ["Case information", "Key questions", "Planning considerations", "Decision checkpoints", "Common mistakes to watch for", "Final planning summary"],
  },
  {
    number: 2,
    title: "The 10-Minute Implant Case Assessment™",
    value: "$37",
    image: "https://cdn.magicpatterns.com/uploads/kM33ekW1NUmfuLxybffXX6/BONUS_2_IMPLANNING-EN.png",
    text: "A rapid assessment framework for the first stage of an implant case. Use it when a patient arrives and you need to quickly identify:",
    bullets: ["What you already know", "What you still need to investigate", "What risks need attention", "Whether the case is ready for deeper planning"],
  },
  {
    number: 3,
    title: "Implant Planning Mistakes Library™",
    value: "$47",
    image: "https://cdn.magicpatterns.com/uploads/vW3PbvKFifknFx2X1sCQXc/BONUS_3_IMPLANNING-EN.png",
    text: "A practical library of common planning mistakes and the questions to ask yourself before proceeding — so you recognize the decision points where mistakes tend to happen.",
    bullets: ["Diagnosis", "Anatomy", "CBCT review", "Implant position", "Prosthetic considerations", "Surgical preparation"],
  },
  {
    number: 4,
    title: "The Implant Case Presentation Kit™",
    value: "$37",
    image: "https://cdn.magicpatterns.com/uploads/6L6Bh3RUJ4ntniZ3H52fUg/BONSU_4_IMPLANNING-EN.png",
    text: "Templates to help you organize and communicate an implant case clearly.",
    bullets: ["Case presentation template", "Patient discussion worksheet", "Treatment planning summary", "Clinical documentation template", "Case review sheet"],
  },
  {
    number: 5,
    title: "30 Professional Implant Checklists™",
    value: "$29",
    image: "https://cdn.magicpatterns.com/uploads/izDz4iT6mK7N3fKbsN59Eq/BONUS_5_IMPLANNING-EN.png",
    text: "A collection of printable checklists covering the major stages of implant planning. Your quick-reference tools before, during and after the planning process.",
    bullets: ["Diagnosis", "Risk assessment", "CBCT analysis", "Planning", "Surgical preparation", "Follow-up"],
  },
];

export const offerFeatures = [
  "Lifetime Access",
  "No Monthly Fees",
  "Instant Digital Access",
  "5 Bonuses Included",
  "7-Day Guarantee",
];

export const testimonials = [
  {
    headline: "I finally have a sequence to follow.",
    name: "Dr. James R.",
    country: "United States",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/1a1d42b8-df3d-477b-9455-6263a2792f78.jpg",
    quote: "I knew the principles of implant planning, but I always felt like I was jumping between different resources. The Playbook gave me a sequence I can actually follow.",
  },
  {
    headline: "The CBCT section changed how I review cases.",
    name: "Dr. Daniel M.",
    country: "Canada",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/23d55c82-bad2-4bf3-8816-9e45b7e0bf62.jpg",
    quote: "The biggest value for me is the way the CBCT review is organized. I now have a checklist of what I need to evaluate instead of simply staring at the scan.",
  },
  {
    headline: "It feels like a clinical reference system.",
    name: "Dr. Andrew P.",
    country: "United Kingdom",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/07f8674d-7d13-42ef-8347-92d9f1dae018.jpg",
    quote: "This doesn't feel like another PDF course. I keep the planning sheets available when I'm working through a case.",
  },
  {
    headline: "The checklists are incredibly practical.",
    name: "Dr. Michael R.",
    country: "Australia",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/4730198c-0389-4a62-8a20-d2684eedb04f.jpg",
    quote: "The checklists make the biggest difference for me. They help me slow down at the right points instead of trying to remember everything.",
  },
  {
    headline: "I bought it because I wanted a workflow.",
    name: "Dr. Chris T.",
    country: "United States",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/3f583a7c-76ec-4ee6-b2bc-228716434c4b.jpg",
    quote: "I wasn't looking for another implantology course. I wanted something practical that would help me organize my planning. That's exactly what this is.",
  },
];

export const faqs = [
  { q: "How will I receive the Implant Planning Playbook™?", a: "Immediately after your payment, you'll receive access instructions by email. The complete Playbook and bonuses are delivered digitally." },
  { q: "Is this a physical book?", a: "No. The Implant Planning Playbook™ is a digital product designed for immediate access and easy reference from your computer, tablet or phone." },
  { q: "Do I need to be an implant specialist?", a: "The Playbook is designed as an educational planning resource for dentists interested in implant treatment. Your appropriate level of training, experience and scope of practice should always determine which clinical procedures you perform." },
  { q: "Is this a replacement for implantology training?", a: "No. The Playbook is a planning and educational framework. It does not replace formal clinical education, supervised training, professional judgment, patient-specific diagnosis or applicable clinical guidelines." },
  { q: "Can I print the checklists?", a: "Yes. The planning worksheets and checklists are designed to be printable." },
  { q: "Is this a one-time payment?", a: "Yes. You pay $27 USD once and receive lifetime access to the product. There are no monthly payments." },
  { q: "What if I don't like it?", a: "You have a 7-day guarantee. Explore the Playbook, use the resources and decide whether it fits your workflow. If it doesn't, contact us within 7 days for a full refund." },
];

export const buyers = [
  { name: "Dr. Alejandro Ruiz", country: "Mexico" },
  { name: "Dra. Valentina Gómez", country: "Colombia" },
  { name: "Dr. Sebastián Torres", country: "Argentina" },
  { name: "Dra. Camila Fernández", country: "Spain" },
  { name: "Dr. Mateo Rojas", country: "Chile" },
  { name: "Dra. Lucía Herrera", country: "Peru" },
  { name: "Dr. Diego Castillo", country: "Ecuador" },
  { name: "Dra. Mariana López", country: "Dominican Republic" },
  { name: "Dr. Andrés Morales", country: "Costa Rica" },
  { name: "Dra. Sofía Vargas", country: "Uruguay" },
  { name: "Dr. Javier Medina", country: "Guatemala" },
  { name: "Dra. Daniela Paredes", country: "Bolivia" },
];
